import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {Account, Timesheet, TimesheetMode, TimesheetStatus} from '@app/spurado';
import {AccountService} from '@api/account.service';
import {TimesheetService} from '@app/service/timesheet.service';
import {BreakpointService} from '@app/service/breakpoint.service';
import {MatDialog} from '@angular/material/dialog';
import {TimesheetSummaryDialogComponent} from '@app/shared/timesheet/summary-dialog/timesheet-summary-dialog.component';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {MatIcon} from '@angular/material/icon';
import {MatMiniFabButton} from '@angular/material/button';
import {FormsModule} from "@angular/forms";
import {ConfirmationComponent} from "@app/shared/confirmation/confirmation.component";
import {NgClass} from "@angular/common";
import {ICITY_ORGANISATION_UUID} from "@app/shared/constants/organisation.constants";

@Component({
    selector: 'spurado-timesheet-status',
    templateUrl: './timesheet-status.component.html',
    styleUrls: ['./timesheet-status.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatIcon,
        TranslateModule,
        MatMiniFabButton,
        FormsModule,
        NgClass,
    ],
})
export class TimesheetStatusComponent implements OnInit {

    @Input()
    timesheet: Timesheet;

    @Input()
    loggedAccount: Account;

    @Input()
    mode: TimesheetMode;

    @Output()
    refresh: EventEmitter<any> = new EventEmitter<any>(true);

    timesheetStatus: typeof TimesheetStatus = TimesheetStatus;
    timesheetMode: typeof TimesheetMode = TimesheetMode;
    initiateRecall: boolean;
    selectedStatus: TimesheetStatus;

    constructor(
        private accountService: AccountService,
        public breakpointService: BreakpointService,
        private timesheetService: TimesheetService,
        protected translateService: TranslateService,
        private timesheetWeekSummary: MatDialog,
        private confirmationDialog: MatDialog
    ) {
    }

    ngOnInit() {
        this.selectedStatus = this.timesheet.status;
    }

    get enableRecall(): boolean {
        return TimesheetMode.ADMIN === this.mode || this.timesheet.enableRecall;
    }

    proposeRecall(newStatus: TimesheetStatus) {
        if (newStatus === this.timesheet.status) {
            return;
        }

        const confirmationRef = this.confirmationDialog.open(ConfirmationComponent, {
            disableClose: true,
            autoFocus: true
        });
        confirmationRef.afterClosed().subscribe(result => {
                if (result) {
                    if (this.enableRecall) {
                        this.initiateRecall = !this.initiateRecall;
                        this.recall();
                    }
                } else {
                    this.selectedStatus = this.timesheet.status;
                }
            }
        );
    }

    recall() {
        if (this.enableRecall) {
            this.timesheetService.recallTimesheet(this.loggedAccount.organisation.uuid, this.timesheet, TimesheetMode.ADMIN === this.mode).then(() => {
                if (TimesheetMode.ADMIN === this.mode) {
                    this.refresh.emit();
                }
            });
        }
    }

    askSummary() {
        this.timesheetService.summary(this.loggedAccount.organisation.uuid, this.timesheet.uuid).subscribe(response => {
            const dialogRef = this.timesheetWeekSummary.open(TimesheetSummaryDialogComponent, {
                autoFocus: true,
                data: {
                    summaries: response,
                    configuration: this.loggedAccount.organisation.configuration,
                    workingHoursPerDay: this.loggedAccount.workingHoursPerDay ?? this.loggedAccount.organisation.configuration.workingHoursPerDay.value
                }
            });

            dialogRef.afterOpened().subscribe(() => {
                // Manually retrigger layout
                requestAnimationFrame(() => {
                    const chips = document.querySelectorAll('spurado-chip .chip');
                    chips.forEach(chip => {
                        chip.classList.remove('force-refresh');
                        chip.classList.add('force-refresh');
                    });
                });
            });
        });
    }

    get availableStatuses(): TimesheetStatus[] {
        const statuses = [];

        if (this.timesheet.status) {
            statuses.push(this.timesheet.status);
        }

        if (this.timesheet.status !== TimesheetStatus.EDITABLE) {
            statuses.push(TimesheetStatus.EDITABLE);
        }

        return statuses;
    }

    get selectedStatusClass(): string {
        switch (this.selectedStatus) {
            case TimesheetStatus.EDITABLE:
                return 'editable';
            case TimesheetStatus.PRE_FINAL:
                return 'pre-final';
            case TimesheetStatus.FINAL:
                return 'final';
            default:
                return '';
        }
    }

    protected readonly ICITY_ORGANISATION_UUID = ICITY_ORGANISATION_UUID;
}
